import React, { useState } from 'react'

import { observer } from 'mobx-react-lite'
import { Instance } from 'mobx-state-tree'

import { Box, DialogActions, Stack, Tab, Tabs } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import { Language, PermMedia, Publish, Search, TextFields } from '@mui/icons-material'

import { Quiz } from '@trivie/core/src/models/Quiz'

import { UploadResourceTab } from './tabs/upload-resource-tab'
import { WebResourceTab } from './tabs/web-resource-tab'
import { TextResourceTab } from './tabs/text-resource-tab'
import { ExistingResourcesTab } from './tabs/existing-resource-tab'
import { useStores } from '@trivie/core'
import { ResourceComponent } from '@trivie/ui-web/components/resource-component'
import { SearchResourceTab } from './tabs/search-resource-tab'
import { Button } from '@trivie/ui-web/components/button'

export const a11yProps = (index: number) => {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  }
}

export const useTabsStyles = makeStyles({
  fixed: {
    display: 'flex',
    justifyContent: 'flex-start',
  },
})

interface ContentGenerationTabProps {
  children?: React.ReactNode
  index: number
  value: number
}

export const ContentGenerationTab = observer((props: ContentGenerationTabProps) => {
  const { children, value, index, ...other } = props

  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <>{children}</>}
    </div>
  )
})

interface ContentGenerationTabsProps {
  quiz: Instance<typeof Quiz> | null
  topic?: any
  standalone?: boolean
  onClose?: any
  onUpload?: any
  selectedTab?: any
  setSelectedTab?: any
  showExisting?: boolean
  card?: boolean
}

export const ContentGenerationTabs = observer((props: ContentGenerationTabsProps) => {
  const { manageResourceStore } = useStores()
  const { getSourceDetails } = manageResourceStore
  const {
    card,
    showExisting,
    onUpload,
    selectedTab,
    setSelectedTab,
    quiz,
    onClose,
    topic,
    standalone,
  } = props
  const [value, setValue] = React.useState(
    selectedTab ? selectedTab : topic || quiz || showExisting ? 0 : 1,
  )
  const tabsClasses = useTabsStyles()

  const [selected, setSelected] = useState<any>(null)

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue)
    if (Boolean(setSelectedTab)) {
      setSelectedTab(newValue)
    }
    setSelected(null)
  }

  const tabSx = {
    '&.Mui-selected': {
      color: '#000000',
    },
    '& > svg': {
      marginRight: '4px !important',
    },
    position: 'relative',
    padding: '6px 8px',
    overflow: 'hidden',
    // letterSpacing: '0px !important',
    maxWidth: '264px',
    minWidth: '0px',
    boxSizing: 'border-box',
    minHeight: '48px !important',
    textAlign: 'center',
    flexShrink: 0,
    whiteSpace: 'normal',
    textTransform: 'none !important',
    justifyContent: 'center',
  }

  return (
    <>
      <Box
        sx={{
          width: '100%',
          height: value === 0 ? '729px' : '100%',
          paddingLeft: standalone ? 0 : '24px',
          paddingRight: standalone ? 0 : '24px',
          display: quiz?.mercuryResource && !standalone && !showExisting ? 'none' : 'block',
        }}
      >
        <Box
          style={{
            marginBottom: 24,
          }}
        >
          <Tabs
            style={{ overflowX: 'auto' }}
            selectionFollowsFocus
            variant="standard"
            indicatorColor="primary"
            classes={{ ...tabsClasses }}
            value={value}
            onChange={handleChange}
            aria-labelledby="a11y-tabs-automatic-label"
          >
            <Tab
              style={{ display: topic || quiz || showExisting ? 'flex' : 'none' }}
              sx={tabSx}
              icon={<PermMedia style={{ fontSize: 16 }} />}
              iconPosition="start"
              disableRipple
              label="Library"
              {...a11yProps(0)}
            />
            {/* <Tab
              style={{ display: card ? 'none' : 'flex' }}
              sx={tabSx}
              icon={<Search style={{ fontSize: 16 }} />}
              iconPosition="start"
              disableRipple
              label="Search"
              {...a11yProps(1)}
            /> */}
            <Tab
              icon={<Publish style={{ fontSize: 16 }} />}
              sx={tabSx}
              iconPosition="start"
              disableRipple
              label="Upload"
              // style={{ marginRight: 8 }}
              {...a11yProps(2)}
            />
            <Tab
              icon={<Language style={{ fontSize: 16 }} />}
              sx={tabSx}
              iconPosition="start"
              disableRipple
              label="Web"
              // style={{ marginRight: 8 }}
              {...a11yProps(3)}
            />
            <Tab
              sx={tabSx}
              style={{ display: card ? 'none' : 'flex' }}
              icon={<TextFields style={{ fontSize: 16 }} />}
              iconPosition="start"
              disableRipple
              label="Text"
              // style={{ marginRight: 8 }}
              {...a11yProps(4)}
            />
          </Tabs>
        </Box>
        <ContentGenerationTab value={value} index={0}>
          <ExistingResourcesTab
            onSelect={(s) => {
              setSelected(s)
            }}
            card={card}
            selected={selected}
            topic={topic}
            standalone={standalone}
            quiz={quiz}
          />
        </ContentGenerationTab>
        {/* <ContentGenerationTab value={value} index={1}>
          <SearchResourceTab
            topic={topic}
            standalone={standalone}
            quiz={quiz}
            onSelect={(s) => {
              setSelected(s)
              if (Boolean(onUpload)) {
                onUpload(s)
              }
              onClose()
            }}
          />
        </ContentGenerationTab> */}
        <ContentGenerationTab value={value} index={1}>
          {selected && standalone ? (
            <ResourceComponent
              inline
              isSelected={false}
              onClick={(r) => {}}
              edit={topic || quiz}
              resource={selected}
              hideActions
            />
          ) : (
            <UploadResourceTab
              onUpload={(s) => {
                if (s) {
                  setSelected(s)
                  s.set('editing', true)
                }
              }}
              card={card}
              topic={topic}
              standalone={standalone}
              quiz={quiz}
            />
          )}
        </ContentGenerationTab>
        <ContentGenerationTab value={value} index={2}>
          {selected && standalone ? (
            <ResourceComponent
              inline
              isSelected={false}
              onClick={(r) => {}}
              edit={topic || quiz}
              resource={selected}
              hideActions
            />
          ) : (
            <WebResourceTab
              onSelect={(s) => {
                setSelected(s)
                s.set('editing', true)
              }}
              selected={selected}
              topic={topic}
              standalone={standalone}
              quiz={quiz}
              card={card}
            />
          )}
        </ContentGenerationTab>
        <ContentGenerationTab value={value} index={3}>
          <TextResourceTab
            onSelect={(s) => {
              setSelected(s)
              if (Boolean(onUpload)) {
                onUpload(s)
              }
              onClose()
            }}
            topic={topic}
            standalone={standalone}
            quiz={quiz}
          />
        </ContentGenerationTab>
      </Box>
      {value === 3
        ? null
        : standalone && (
            <Stack mt="20px" direction="row" justifyContent="flex-end">
              <Button
                disabled={!selected}
                onClick={() => {
                  if (selected) {
                    getSourceDetails(selected.source_id).then((details) => {
                      selected.set(
                        'preview_url',
                        details?.converted_pdf_url || details?.filename_or_url,
                      )
                      if (topic && !card) {
                        topic.addResource(selected)
                      } else if (quiz && !card) {
                        quiz.addResource(selected)
                      }
                    })
                    if (Boolean(onUpload)) {
                      onUpload(selected)
                    }
                    onClose()
                  }
                }}
                text={selected || value === 0 || value === 3 ? 'Add Source' : 'Next'}
              />
            </Stack>
          )}
    </>
  )
})
